import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { trpc } from "@calcom/trpc/react";
import { Button } from "@calcom/ui";

import GiactAlertDialog from "@components/dialog/GiactAlertDialog";

type PayrixFormProps = {
  apiKey: string;
  environment: string;
  merchant: string;
  buttonText: string;
  paymentProcessing?: boolean;
  onSuccess: (token: string) => void;
  setPaymentError: (error: string) => void;
  setPaymentProcessing: (processing: boolean) => void;
  enableACH?: boolean;
  setIsACHPayment?: (isACHPayment: boolean) => void;
  disabled?: boolean;
};

export default function PayrixForm({
  apiKey,
  environment,
  merchant,
  buttonText,
  onSuccess,
  paymentProcessing,
  setPaymentError,
  setPaymentProcessing,
  enableACH = true,
  setIsACHPayment,
  disabled,
}: PayrixFormProps) {
  const payFields = useRef<any>(null);
  /** Synchronous guard: `paymentProcessing` can lag a frame, so double-clicks could still call submit twice. */
  const submitInFlightRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<"card" | "bank">("card");
  const [giactAlertOpen, setGiactAlertOpen] = useState(false);

  const { mutateAsync: verifyGiact } = trpc.viewer.payments.verifyGiact.useMutation();

  // Keep the ref updated with the latest onSuccess function
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  const loadScripts = useCallback(async () => {
    const scripts = [
      {
        id: "payrixScript",
        src: `https://${environment === "production" ? "api" : "test-api"}.payrix.com/payFieldsScript?spa=1`,
        onLoad: () => {
          payFields.current = (window as any).PayFields;
          console.log("PayFields script loaded");
        },
      },
    ];

    const load = ({ id, src, onLoad }: { id: string; src: string; onLoad: () => void }) =>
      new Promise<void>((resolve, reject) => {
        if (document.getElementById(id)) return resolve();

        const script = document.createElement("script");
        script.src = src;
        script.id = id;
        script.async = true;
        script.onload = () => {
          onLoad?.();
          resolve();
        };
        script.onerror = () => reject(new Error(`Failed to load script: ${id}`));
        document.body.appendChild(script);
      });

    for (const script of scripts) {
      await load(script);
    }
  }, [environment]);

  function removePayFields() {
    const script = document.getElementById("payrixScript");
    if (script) {
      if (payFields.current && typeof payFields.current.unmountAll === "function") {
        payFields.current.unmountAll();
      }
      script.remove();
    }
    console.log("PayFields script removed");
  }

  const handleClick = useCallback(() => {
    if (disabled) return;
    if (!payFields.current) return;
    if (paymentProcessing || submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setPaymentProcessing(true);
    try {
      payFields.current.submit();
    } catch (error) {
      submitInFlightRef.current = false;
      setPaymentProcessing(false);
      setPaymentError(error instanceof Error ? error.message : "Something went wrong");
    }
  }, [disabled, paymentProcessing, setPaymentError, setPaymentProcessing]);

  const handleClickRef = useRef(handleClick);
  useEffect(() => {
    handleClickRef.current = handleClick;
  }, [handleClick]);

  const debouncedHandleClick = useMemo(
    () => debounce(() => handleClickRef.current(), 300, { leading: true, trailing: false }),
    []
  );

  useEffect(() => {
    return () => debouncedHandleClick.cancel();
  }, [debouncedHandleClick]);

  useEffect(() => {
    const initScript = async () => {
      await loadScripts();

      if (!payFields) {
        console.log("Error Loading PayFields");
        return;
      }

      payFields.current.config.apiKey = apiKey;
      payFields.current.config.merchant = merchant;

      payFields.current.customizations = {
        style: {
          ".input": {
            width: "100%",
          },
        },
      };

      if (selectedPaymentMethod === "card") {
        payFields.current.config.mode = "token";
        // payFields.current.config.allowedCardBrands = ["VISA", "MASTERCARD", "AMEX", "DISCOVER"];
        payFields.current.config.standAloneZip = true;
        payFields.current.config.standAloneAddress = false;

        payFields.current.fields = [
          { type: "number", element: "#number" },
          { type: "cvv", element: "#cvv" },
          { type: "expiration", element: "#expiration" },
          { type: "address", element: "#address" },
        ];

        payFields.current.customizations.optionalFields = [
          "#address1",
          "#city",
          "#state",
          "#email",
          "#phone",
        ];
      } else {
        payFields.current.config.mode = "txnToken";
        payFields.current.config.txnType = "ecsale";
        payFields.current.config.amount = "0";

        payFields.current.fields = [
          {
            type: "name",
            element: "#name",
          },
          {
            type: "routing",
            element: "#routing",
          },
          {
            type: "account_type",
            element: "#account_type",
          },
          {
            type: "account_number",
            element: "#account_number",
          },
        ];
      }

      payFields.current.onSuccess = (response: any) => {
        console.log("onSuccess", response);
        if (selectedPaymentMethod === "card") {
          onSuccessRef.current(response.data[0].id);
        } else {
          onSuccessRef.current(response.data[0].token.id);
        }
        setTimeout(() => {
          submitInFlightRef.current = false;
        }, 1000);
      };

      payFields.current.onFailure = async (response: any) => {
        submitInFlightRef.current = false;
        console.log("onFailure", response);
        console.log("payFields.current.config.mode", payFields.current.config.mode);
        if (payFields.current.config.txnType === "ecsale") {
          try {
            const giactResult = await verifyGiact({ transactionId: response.data[0].id });
            console.log("giactResult", giactResult);
            if (giactResult === "BLOCK") {
              setGiactAlertOpen(true);
            }
          } catch (error) {
            console.log("error", error);
          }
        }
        if (response.errors) {
          setPaymentError(response.errors[0].msg);
        } else {
          setPaymentError("Something went wrong");
        }
        setPaymentProcessing(false);
      };

      payFields.current.onValidationFailure = () => {
        submitInFlightRef.current = false;
        setPaymentError("Invalid card details");
        setPaymentProcessing(false);
        console.log("onvalidationFailure");
      };

      // console.log({ payfields: payFields.current });
      payFields.current.ready();
    };

    const timeout = setTimeout(() => {
      initScript();
    }, 1000);

    return () => {
      submitInFlightRef.current = false;
      removePayFields();
      clearTimeout(timeout);
    };
  }, [selectedPaymentMethod]);
  return (
    <>
      <div className="main w-full">
        <div className="payfields w-full">
          <form className="m-4">
            <div className={`mb-6 flex gap-2 ${enableACH ? "" : "hidden"}`}>
              <div className="flex-1">
                <Button
                  type="button"
                  onClick={() => {
                    setSelectedPaymentMethod("card");
                    setIsACHPayment?.(false);
                  }}
                  className={`flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 font-medium transition-all duration-200 ${
                    selectedPaymentMethod === "card"
                      ? "border-blue-500 bg-blue-50 text-blue-700 shadow-md"
                      : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                  style={{
                    boxShadow:
                      selectedPaymentMethod === "card"
                        ? "0 4px 6px -1px rgba(59, 130, 246, 0.1), 0 2px 4px -1px rgba(59, 130, 246, 0.06)"
                        : "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                  }}>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                  Card
                </Button>
              </div>
              <div className="flex-1">
                <Button
                  type="button"
                  onClick={() => {
                    setSelectedPaymentMethod("bank");
                    setIsACHPayment?.(true);
                  }}
                  className={`flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 font-medium transition-all duration-200 ${
                    selectedPaymentMethod === "bank"
                      ? "border-blue-500 bg-blue-50 text-blue-700 shadow-md"
                      : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                  style={{
                    boxShadow:
                      selectedPaymentMethod === "bank"
                        ? "0 4px 6px -1px rgba(59, 130, 246, 0.1), 0 2px 4px -1px rgba(59, 130, 246, 0.06)"
                        : "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                  }}>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  Bank Account
                </Button>
              </div>
            </div>
            <div id="form-container" className="grid w-full grid-cols-2 gap-8">
              {selectedPaymentMethod === "card" && (
                <>
                  <div className="col-span-2 h-12">
                    <label className="text-sm text-gray-500" htmlFor="number">
                      Card Number
                    </label>
                    <div id="number" className="form-row" />
                  </div>

                  <div className="col-span-1 h-12">
                    <label className="text-sm text-gray-500" htmlFor="expiration">
                      Expiration
                    </label>
                    <div id="expiration" className="form-row" />
                  </div>

                  <div className="col-span-1 h-12">
                    <label className="text-sm text-gray-500" htmlFor="cvv">
                      CVV
                    </label>
                    <div id="cvv" className="form-row" />
                  </div>
                  <div className="col-span-2 h-12">
                    <label className="text-sm text-gray-500" htmlFor="address">
                      Zip Code
                    </label>
                    <div id="address" className="form-row" />
                  </div>
                </>
              )}
              {selectedPaymentMethod === "bank" && (
                <>
                  <div id="number" className="hidden" />
                  {/* <div id="expiration" className="hidden" /> */}
                  {/* <div id="cvv" className="hidden" /> */}
                  <div className="col-span-2 h-12">
                    <label className="text-sm text-gray-500" htmlFor="name">
                      Name
                    </label>
                    <div id="name" className="form-row" />
                  </div>
                  <div className="col-span-2 h-12">
                    <label className="text-sm text-gray-500" htmlFor="account_number">
                      Account Number
                    </label>
                    <div id="account_number" className="form-row" />
                  </div>
                  <div className="col-span-2 h-12">
                    <label className="text-sm text-gray-500" htmlFor="account_type">
                      Account Type
                    </label>
                    <div id="account_type" className="form-row" />
                  </div>
                  <div className="col-span-2 h-12">
                    <label className="text-sm text-gray-500" htmlFor="routing">
                      Routing Number
                    </label>
                    <div id="routing" className="form-row" />
                  </div>
                </>
              )}
            </div>
          </form>
          <div className="button-container mt-10">
            <Button
              type="button"
              id="button"
              className="mt-4 h-10 w-full justify-center bg-black p-3 text-sm font-bold hover:bg-black"
              name="Submit"
              loading={paymentProcessing || submitInFlightRef.current}
              onClick={debouncedHandleClick}
              disabled={disabled}>
              {buttonText}
            </Button>
          </div>
          <div id="googlePayButton" />
        </div>
      </div>
      <GiactAlertDialog open={giactAlertOpen} handler={setGiactAlertOpen} />
    </>
  );
}
